import React from 'react';
import { Typography } from '@mui/material';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { FDataGrid, FGridMoneyCell } from '../grid';
type Props = {
  rows: any[];
  loading?: boolean;
  readOnly?: boolean;
  onChange: (rows: any[]) => void;
  onSubmit: (rows: any[]) => Promise<void>;
};
type State = { saving: boolean };
export default class PpmLinesGrid extends React.PureComponent<Props, State> {
  state = { saving: false };
  private validate = (r: any) =>
    !r.atx
      ? 'Activity description is required.'
      : !r.pmd
        ? 'Procurement method is required.'
        : !r.psd || !r.eed
          ? 'Start and end dates are required.'
          : '';
  private processRowUpdate = (newRow: GridRowModel) => {
    if (this.props.readOnly) return newRow;
    const u:any = {
      ...newRow,
      _state: 'dirty',
      __dirty: true,
      __error: this.validate(newRow),
    };
    const rs = (this.props.rows || []).map((r: any) =>
      String(r.rid || r.id) === String(u.rid || u.id) ? u : r
    );
    this.props.onChange(rs);
    return u;
  };
  private save = async () => {
    this.setState({ saving: true });
    try {
      await this.props.onSubmit(
        (this.props.rows || []).filter(
          (r: any) => r.__dirty || r._state === 'dirty'
        )
      );
    } finally {
      this.setState({ saving: false });
    }
  };
  private columns = (): GridColDef[] => {
    const e = !this.props.readOnly;
    return [
      {
        field: 'psc',
        headerName: 'Section',
        width: 140,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'ccc',
        headerName: 'CC',
        width: 110,
        cellClassName: 'ppm-master',
      },
      {
        field: 'acc',
        headerName: 'Account',
        width: 120,
        cellClassName: 'ppm-master',
      },
      {
        field: 'bga',
        headerName: 'Budget',
        width: 140,
        renderCell: FGridMoneyCell,
        cellClassName: 'ppm-computed',
      },
      {
        field: 'ara',
        headerName: 'Arrears',
        width: 120,
        type: 'number',
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'avc',
        headerName: 'Available',
        width: 140,
        renderCell: FGridMoneyCell,
        cellClassName: 'ppm-computed',
      },
      {
        field: 'atx',
        headerName: 'Procurement Activity',
        flex: 1,
        minWidth: 280,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'pnt',
        headerName: 'Nature',
        width: 180,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'pmd',
        headerName: 'Method',
        width: 150,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'rvt',
        headerName: 'Review',
        width: 120,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'tpd',
        headerName: 'Publication',
        width: 140,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'bsd',
        headerName: 'Bid Deadline',
        width: 140,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'evd',
        headerName: 'Eval Days',
        width: 110,
        type: 'number',
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'psd',
        headerName: 'Start',
        width: 130,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'exd',
        headerName: 'Exec Days',
        width: 110,
        type: 'number',
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'eed',
        headerName: 'End',
        width: 130,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'lot',
        headerName: 'Lot',
        width: 100,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'ruc',
        headerName: 'Responsible Unit',
        width: 170,
        editable: e,
        cellClassName: 'ppm-input',
      },
      {
        field: 'san',
        headerName: 'Superactivity',
        width: 180,
        cellClassName: 'ppm-master',
      },
      {
        field: '__error',
        headerName: 'Validation',
        minWidth: 220,
        renderCell: (p) =>
          p.value ? (
            <Typography variant="caption" color="error.main">
              {p.value}
            </Typography>
          ) : null,
      },
    ];
  };
  render() {
    const dirty = (this.props.rows || []).some(
      (r: any) => r.__dirty || r._state === 'dirty'
    );
    const invalid = (this.props.rows || []).some(
      (r: any) => r.__error || this.validate(r)
    );
    return (
      <FDataGrid
        title={
          this.props.readOnly
            ? 'PPM Lines — Read Only'
            : 'PPM Preparation Lines'
        }
        subtitle="Budget facts are read-only. Procurement classification, method and calendar are prepared here."
        rows={this.props.rows || []}
        columns={this.columns()}
        loading={this.props.loading || this.state.saving}
        height={560}
        getRowId={(r: any) => r.rid || r.id}
        processRowUpdate={
          this.props.readOnly ? undefined : this.processRowUpdate
        }
        saveAll={
          !this.props.readOnly && dirty && !invalid ? this.save : undefined
        }
        sx={{
          '& .ppm-input': { bgcolor: 'warning.lighter', fontWeight: 600 },
          '& .ppm-master': { bgcolor: 'info.lighter' },
          '& .ppm-computed': { bgcolor: 'success.lighter', fontWeight: 600 },
          '& .MuiDataGrid-row.ppm-error': { bgcolor: 'error.lighter' },
        }}
      />
    );
  }
}
